import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useTerritory } from "@/contexts/TerritoryContext";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";
import { AuthShell, PasswordInput, fieldClass } from "@/components/auth/AuthShell";
import { BookOpen, Trophy, Users } from "lucide-react";

export default function Login() {
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force password reset modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const { login, isAuthenticated, isLoading } = useAuth();
  const { territory } = useTerritory();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getReturnTo = () => {
    const raw = new URLSearchParams(window.location.search).get('returnTo') || '';
    return raw.startsWith('/') ? raw : '/dashboard';
  };

  // Redirect if already authenticated (suppress while modal is open)
  useEffect(() => {
    if (isAuthenticated && !showResetModal) {
      setLocation(getReturnTo());
    }
  }, [isAuthenticated, showResetModal, setLocation]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await login(formData.identifier, formData.password);

      // Check if admin flagged this account for a forced reset
      const cached = queryClient.getQueryData<{ user: { forcePasswordReset?: boolean } }>(['/api/auth/me']);
      if (cached?.user?.forcePasswordReset) {
        setShowResetModal(true);
      } else {
        toast({ title: "Welcome back!", description: "You've successfully signed in to your account." });
        setLocation(getReturnTo());
      }
    } catch (error: any) {
      toast({
        title: "Sign in failed",
        description: error.message || "We couldn't match that. Check the spelling, or use \u201cI can't remember what I used\u201d below.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }

    setIsResetting(true);
    try {
      const res = await apiRequest('POST', '/api/auth/complete-password-reset', { newPassword });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update password');
      }
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      setShowResetModal(false);
      toast({ title: "Password updated!", description: "Your new password has been saved." });
      setLocation(getReturnTo());
    } catch (error: any) {
      toast({ title: "Failed to update password", description: error.message, variant: "destructive" });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      <AuthShell
        illustration="owl-wave"
        headline="Welcome back."
        subtitle={`Pick up your ${territory.examName} practice where you left off. Owlbert kept your streak warm.`}
        features={[
          { icon: <BookOpen className="w-5 h-5" />, title: "Every subject, every topic", text: "Quizzes, lessons and readers built for the exam." },
          { icon: <Trophy className="w-5 h-5" />, title: "XP, badges and real prizes", text: "Finish tests to climb the board and win gift cards." },
          { icon: <Users className="w-5 h-5" />, title: "Learn with friends", text: "Follow classmates, send challenges, share the wins." },
        ]}
        formTitle="Sign in"
        formSubtitle="Sign in with your email address or your username."
        footer={
          <>
            <p>New here? <button type="button" className="text-primary font-semibold hover:underline" onClick={() => setLocation('/signup')} data-testid="link-get-started">Create an account</button></p>
            <p className="mt-1"><button type="button" className="text-muted-foreground hover:text-primary hover:underline" onClick={() => setLocation('/find-account')} data-testid="link-find-account">I can't remember what I used</button></p>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="identifier" className="text-sm font-semibold">Email or username</Label>
            {/* Deliberately type="text": this field takes a username too, and type="email"
                made the browser reject one before it ever reached the server. */}
            <Input id="identifier" type="text" placeholder="you@example.com or your username" value={formData.identifier} onChange={(e) => handleChange('identifier', e.target.value)} required autoComplete="username" className={fieldClass} data-testid="input-email" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setLocation('/forgot-password')} data-testid="link-forgot-password">Forgot your password?</button>
            </div>
            <PasswordInput id="password" placeholder="Your password" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} required autoComplete="current-password" data-testid="input-password" />
          </div>
          <Button type="submit" disabled={isSubmitting || isLoading} className="w-full h-12 text-base rounded-xl" data-testid="button-sign-in">
            {isSubmitting || isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </AuthShell>

      {/* Mandatory password reset modal: shown when admin reset the user's password */}
      <Dialog open={showResetModal}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Set a New Password</DialogTitle>
            <DialogDescription>
              Your password was recently reset by an administrator. Please choose a new password to continue.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCompleteReset} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="h-12"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Repeat your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-12"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isResetting}>
              {isResetting ? 'Saving...' : 'Save New Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
